/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {
  useCallback,
  useEffect,
  useState
} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {AccountLink, Asset, CRUDPermissions} from '@ion/core/src/types/entities';
import {DropDownInfiniteScrollProps} from '@ion/dls/src/components/menus/Dropdown';
import {AssetAssociation, AssetEditor, AssetEditorShowDefaultProps, AssetTreeRefresh} from '@ion/dls/src/editors/AssetEditor';
import {useAuthenticatedUser} from '../users/useAuthenticatedUser';
import {addAdminAsset, addAsset} from './state';

export interface AddAssetEditorProps extends AssetAssociation,
  AssetEditorShowDefaultProps,
  DropDownInfiniteScrollProps,
  AssetTreeRefresh {
  globalGroup: AccountLink;
  isCtcAdmin?: boolean;
  includeGroupProperties?: boolean;
  includeDeviceGridCheckboxes?: boolean;
  includeAssetGridCheckboxes?: boolean;
  assetsPermissions?: CRUDPermissions;
}

/**
 * AddAssetEditor
 */
export const AddAssetEditor = ({
  globalGroup,
  showAddTypeDefault,
  showAddInstallDateDefault,
  assetsPermissions,
  isCtcAdmin = false,
  includeAssetAssociation = false,
  isReadOnly = false,
  includeGroupProperties = true,
  includeDeviceGridCheckboxes = false,
  includeAssetGridCheckboxes = false,
  isInfiniteScroll = false,
  showTreeRefresh = false
}: AddAssetEditorProps) => {
  const dispatch                      = useDispatch();
  const {account}                     = useAuthenticatedUser();
  const {result: addedAsset, loading} = useSelector(({adminAssets: {add}}) => add);
  const [clearForm, setClearForm]     = useState(false);

  useEffect(() => {
    if (addedAsset) {
      setClearForm(true);
      dispatch(addAdminAsset(false));
    }
  }, [addedAsset]);

  const addNewAsset = useCallback((asset: Asset) => {
    dispatch(addAsset(asset, showTreeRefresh));
  }, []);

  const onClose = useCallback(() => {
    dispatch(addAdminAsset(false));
  }, []);

  return (
    <AssetEditor open
      account={account}
      isReadOnly={isReadOnly}
      defaultAccount={globalGroup}
      onSubmit={addNewAsset}
      onCloseDialog={onClose}
      clear={clearForm}
      loading={loading}
      isCtcAdmin={isCtcAdmin}
      assetsPermissions={assetsPermissions}
      showAddTypeDefault={showAddTypeDefault}
      showAddInstallDateDefault={showAddInstallDateDefault}
      includeAssetAssociation={includeAssetAssociation}
      includeGroupProperties={includeGroupProperties}
      includeDeviceGridCheckboxes={includeDeviceGridCheckboxes}
      includeAssetGridCheckboxes={includeAssetGridCheckboxes}
      isInfiniteScroll={isInfiniteScroll} />
  );
};

export default AddAssetEditor;
